import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  FileText,
  Users,
  CreditCard,
  AlertTriangle,
  Edit,
  Server,
} from "lucide-react";

const TermsPage = () => {
  const platformName = "PartsLokator";

  const sections = [
    {
      icon: <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "1. Introduction",
      content: `Welcome to ${platformName}. By using our website, services, or tools, you agree to these Terms of Use and our Privacy Policy. If you do not agree, please do not use the platform.`,
    },
    {
      icon: <Server className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "2. Platform Purpose",
      content: `${platformName} connects businesses selling recycled auto parts with customers through an AI-powered search and messaging system. We do not take responsibility for the condition, shipment, or payment of parts exchanged on our platform.`,
    },
    {
      icon: <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "3. User Responsibilities",
      content: (
        <>
          <p className="font-semibold mb-1">Sellers agree to:</p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>
              Only list parts that are in their possession and accurately
              described.
            </li>
            <li>Clearly state return or warranty policies if applicable.</li>
            <li>Conduct all transactions honestly.</li>
          </ul>
          <p className="font-semibold mt-3 mb-1">Buyers agree to:</p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>Confirm details with the seller before payment.</li>
            <li>Only contact sellers with serious intent to purchase.</li>
            <li>Follow all community conduct guidelines.</li>
          </ul>
        </>
      ),
    },
    {
      icon: <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "4. Payments & Disputes",
      content: `All payments are handled directly between buyers and sellers. ${platformName} does not mediate financial transactions or disputes. Users are responsible for verifying each other's identity and confirming part details before payment.`,
    },
    {
      icon: <ShieldCheck className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "5. Warranty & Returns",
      content: `Warranty terms are determined by each seller. ${platformName} does not guarantee the condition of parts or enforce return policies.`,
    },
    {
      icon: <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "6. Prohibited Behavior",
      content:
        "We reserve the right to suspend or terminate users who engage in fraud, scams, or misleading listings, harass or abuse others, or attempt to bypass platform rules or fees.",
    },
    {
      icon: <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "7. Liability",
      content: `${platformName} is not liable for losses due to part defects or shipping errors, miscommunications between users, or unauthorized payments made off-platform.`,
    },
    {
      icon: <Edit className="h-6 w-6 sm:h-8 sm:w-8  text-primary" />,
      title: "8. Reporting & Moderation",
      content:
        "Users may be reported for misconduct. Our team will investigate and may take action including warnings, suspensions, or bans.",
    },
    {
      icon: <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />,
      title: "9. Updates",
      content:
        "We may update these terms at any time. Continued use of the platform after changes means you accept the updated terms.",
    },
  ];

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const sectionVariants = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, delay: 0.2 } },
  };

  return (
    <motion.div
      className="w-full" // Removed container, px, py
      initial="initial"
      animate="animate"
      variants={pageVariants}
    >
      <header className="mb-12 text-center">
        <motion.h1
          className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-primary via-purple-500 to-accent mb-4"
          variants={sectionVariants}
        >
          Terms and Conditions
        </motion.h1>
        <motion.p
          className="text-base sm:text-lg text-muted-foreground"
          variants={sectionVariants}
        >
          Last updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </motion.p>
      </header>

      <div className="space-y-8 sm:space-y-10">
        {sections.map((section, index) => (
          <motion.section
            key={index}
            className="p-4 sm:p-6 bg-card rounded-xl shadow-lg border border-border/20"
            variants={sectionVariants}
            custom={index}
            initial="initial"
            animate="animate"
            transition={{ delay: index * 0.1 + 0.3 }}
          >
            <div className="flex flex-col xs:flex-row items-start gap-4 xs:space-x-4">
              <div className="flex-shrink-0 p-3 bg-primary/10 rounded-full">
                {section.icon}
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-3">
                  {section.title}
                </h2>
                <div className="text-muted-foreground leading-relaxed space-y-2 text-sm sm:text-base">
                  {typeof section.content === "string" ? (
                    <p>{section.content}</p>
                  ) : (
                    section.content
                  )}
                </div>
              </div>
            </div>
          </motion.section>
        ))}
      </div>

      <motion.div
        className="mt-8 sm:mt-12 text-center text-sm text-muted-foreground p-6 bg-card rounded-xl shadow-lg border border-border/20"
        variants={sectionVariants}
        transition={{ delay: sections.length * 0.1 + 0.4 }}
      >
        <p>If you have any questions about these Terms, please contact us.</p>
        <p>Thank you for using {platformName}!</p>
      </motion.div>
    </motion.div>
  );
};

export default TermsPage;
